import React, { useEffect, useState } from "react";
import { Trash2, ImagePlus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AdminGallery() {
  const [hero, setHero] = useState("");
  const [gallery, setGallery] = useState([]);
  const [newImage, setNewImage] = useState("");
  const [savingHero, setSavingHero] = useState(false);
  const [banner, setBanner] = useState("");
  const [savingBanner, setSavingBanner] = useState(false);

  const load = () =>
    api.get("/settings").then((res) => {
      setHero(res.data.hero_image || "");
      setGallery(res.data.gallery || []);
      setBanner(res.data.offer_banner_image || "");
    });
  useEffect(() => { load(); }, []);

  const saveHero = async () => {
    if (!hero) { toast.error("Alege o imagine principală"); return; }
    setSavingHero(true);
    try {
      await api.put("/settings/hero", { image: hero });
      toast.success("Imagine principală actualizată");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingHero(false);
    }
  };

  const saveBanner = async () => {
    if (!banner) { toast.error("Alege un banner pentru promoție"); return; }
    setSavingBanner(true);
    try {
      await api.put("/settings/offer-banner", { image: banner });
      toast.success("Banner promoție actualizat");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingBanner(false);
    }
  };

  const addImage = async () => {
    if (!newImage) { toast.error("Încarcă sau adaugă o imagine"); return; }
    try {
      const res = await api.post("/settings/gallery", { image: newImage });
      setGallery((g) => [...g, res.data]);
      setNewImage("");
      toast.success("Poză adăugată în galerie");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeImage = async (id) => {
    try {
      await api.delete(`/settings/gallery/${id}`);
      setGallery((g) => g.filter((x) => x.id !== id));
      toast.success("Poză ștearsă");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-gallery">
      <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Galerie & Imagini</h1>
      <p className="mt-1 text-slate-500">Schimbă poza principală și gestionează galeria de pe prima pagină.</p>

      {/* HERO */}
      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-display text-lg font-bold text-slate-900">Poza principală (hero)</h2>
        <p className="mb-4 text-sm text-slate-500">Imaginea mare din partea de sus a paginii principale.</p>
        <div className="max-w-md">
          <ImageUpload value={hero} onChange={setHero} testId="hero-image-upload" />
        </div>
        <Button onClick={saveHero} disabled={savingHero} className="mt-4 rounded-full bg-sky-600 hover:bg-sky-500" data-testid="save-hero-btn">
          {savingHero ? "Se salvează..." : "Salvează poza principală"}
        </Button>
      </div>

      {/* OFFER BANNER */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="offer-banner-editor">
        <h2 className="font-display text-lg font-bold text-slate-900">Banner promoție „2+1 GRATIS"</h2>
        <p className="mb-4 text-sm text-slate-500">Imaginea mare colorată de pe prima pagină, care duce la pagina Oferte.</p>
        <div className="max-w-2xl">
          <ImageUpload value={banner} onChange={setBanner} testId="offer-banner-upload" />
        </div>
        <Button onClick={saveBanner} disabled={savingBanner} className="mt-4 rounded-full bg-sky-600 hover:bg-sky-500" data-testid="save-banner-btn">
          {savingBanner ? "Se salvează..." : "Salvează bannerul"}
        </Button>
      </div>

      {/* GALLERY */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-display text-lg font-bold text-slate-900">Galerie de poze</h2>
        <p className="mb-4 text-sm text-slate-500">Adaugă câte poze vrei. Apar într-o secțiune pe prima pagină.</p>

        <div className="flex flex-col gap-3 rounded-xl border border-dashed border-slate-200 p-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <ImageUpload value={newImage} onChange={setNewImage} testId="gallery-image-upload" />
          </div>
          <Button onClick={addImage} className="rounded-full bg-emerald-600 hover:bg-emerald-500" data-testid="add-gallery-btn">
            <ImagePlus className="mr-1.5 h-4 w-4" /> Adaugă în galerie
          </Button>
        </div>

        {gallery.length === 0 ? (
          <p className="mt-6 text-center text-sm text-slate-400">Nu ai adăugat nicio poză încă.</p>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {gallery.map((g) => (
              <div key={g.id} className="group relative aspect-square overflow-hidden rounded-xl border border-slate-100 bg-slate-50" data-testid={`gallery-item-${g.id}`}>
                <img src={g.url} alt="Galerie" className="h-full w-full object-cover" />
                <button
                  onClick={() => removeImage(g.id)}
                  className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-red-600 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  data-testid={`gallery-delete-${g.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
